/**
 * stencil/index.js — прибор: настройки, превью, проверка, файлы.
 *
 * ПРЕВЬЮ ПОКАЗЫВАЕТ ТРАФАРЕТ, А НЕ ФАЙЛ РЕЗА. Человек держит в руках лист
 * материала с дырками, поэтому по умолчанию рисуется именно он: лист цвета
 * выбранного материала с тенью, в прорезях — стена, перемычки видны как
 * полоски листа. (До 13.09.2026 здесь были чёрные буквы на белом — ровно
 * напечатанная надпись, «на трафарет не похоже».) Файл PNG — чертёж: чёрный
 * вырез на белом. Вторая вкладка —
 * «как ляжет краска»: та же маска в обратной полярности. Контур реза нужен
 * машине, а не глазу, и показывается только когда его просят.
 *
 * ОПАСНЫЕ МЕСТА ПОДСВЕЧИВАЮТСЯ НА САМОМ РИСУНКЕ. Число «самое тонкое место
 * 0.8 мм» не чинится: непонятно, где оно. Красное пятно на превью чинится за
 * десять секунд — подвинуть букву, поднять размер, сменить материал.
 *
 * ⚠️ Превью обязано иметь МАСШТАБ. Разбор 12.09.2026 (/signature-maker): лист
 * A4, показанный в 278 px, — это 33.6 dpi, и на нём не виден НИ ОДИН дефект
 * толщины. Поэтому здесь есть полоса «как в жизни»: кусок трафарета в
 * настоящем размере, по которому видно и толщину перемычки, и мелкую деталь.
 */
import { buildStencil, setFaceReadyHook, measureSource } from './pipeline.js';
import { toSvg, toDxf, planTiles } from './export.js';
import { buildPdf, buildPdfSet } from './pdfout.js';
import { setChars, planCharSet } from './set.js';
import { SHAPES, SHAPE_GROUPS, shapeById, matchShapes } from './shapes.js';
import { MATERIALS } from './verify.js';

const $ = (id) => document.getElementById(id);
const T = (window.__STENCIL__ && window.__STENCIL__.t) || {};
const SLUG = 'stencil-maker';

const state = {
    kind: 'text',
    /* Текст по умолчанию приходит ИЗ ЛОКАЛИ. Зашитая здесь кириллица
       встречала англичанина, немца и араба словом на чужом алфавите — и это
       не косметика: у арабского и корейского другое направление и другие
       гарнитуры, то есть первый экран показывал бы не работу инструмента, а
       поломку. Слово подобрано коротким и с замкнутым просветом, чтобы прибор
       сразу показал перемычку на деле. */
    text: (window.__STENCIL__ && window.__STENCIL__.t && window.__STENCIL__.t.default_text) || 'STOP',
    face: 'oswald',
    align: 'center',
    track: 0,
    lineHeight: 1.16,
    shape: 'maple',
    bitmap: null,
    fileName: '',

    unit: 'mm',
    widthMm: 200,
    heightMm: 120,
    lockRatio: true,
    /* Скругление углов простой фигуры, мм (квадрат, треугольник). 0 — острые. */
    cornerMm: 0,
    /* Какое измерение ведёт при сохранённых пропорциях: 'w' — ширина,
       'h' — высота, 'box' — вписать в обе (кнопки листов). Лист под рисунок
       подгоняет сборка, а не этот файл: пропорции рисунка знает только она. */
    fitBy: 'w',
    marginMm: 10,
    material: 'mylar',

    bridges: true,
    bridgeMm: 2.5,
    bridgeDensityMm: 38,
    maxPerIsland: 4,
    removed: [],
    manual: [],

    mode: 'threshold',
    threshold: null,
    edgeMm: 1.6,
    growMm: 0,
    invert: false,
    mirror: false,
    smooth: 1,

    view: 'stencil',
    showRisk: true,
    pageId: 'a4',
    landscape: false,
    overlapMm: 10,
    tile: true,
    ruler: true,
    map: true,
};

const PAGES = {
    a4: { wMm: 210, hMm: 297 },
    a3: { wMm: 297, hMm: 420 },
    letter: { wMm: 215.9, hMm: 279.4 },
    legal: { wMm: 215.9, hMm: 355.6 },
    a5: { wMm: 148, hMm: 210 },
};

let last = null;          // результат последней сборки

/* ── телеметрия ───────────────────────────────────────────────────────────
   Сигналы, по которым принимаются решения, а не счётчик нажатий:
   · verdict — что говорит проверка, и только при СМЕНЕ вердикта (не на
     каждую букву): слушают ли люди «порвётся» или режут как есть;
   · export с вердиктом на момент скачивания — тот же вопрос, но про деньги
     и время человека: ушёл ли в файл трафарет, который прибор забраковал;
   · first_build / slow_build — сколько ждёт человек (замер на iPhone и на
     слабой машине), и на каких размерах сборка перестаёт быть живой;
   · face_swapped / glyph_missing — какие письменности не покрывают наши
     шрифты: это решение о том, какой шрифт добавлять следующим;
   · export_failed — отказ выдачи, которого человек мог не понять. */
const TEL = { builds: 0, slow: 0, verdicts: 0, lastSig: null, swaps: new Set(), missing: new Set() };

function issueSig(res) {
    if (!res) return '';
    const kinds = new Set(res.report.issues.map(i => i.kind));
    if (res.unresolved.length) kinds.add('nobridge');
    return [...kinds].sort().join('+');
}

function scriptOf(chars) {
    const c = String(chars || '');
    if (/[\u0600-\u06ff]/.test(c)) return 'arabic';
    if (/[\uac00-\ud7af]/.test(c)) return 'hangul';
    if (/[\u3040-\u30ff]/.test(c)) return 'kana';
    if (/[\u4e00-\u9fff]/.test(c)) return 'han';
    if (/[\u0590-\u05ff]/.test(c)) return 'hebrew';
    if (/[\u0900-\u097f]/.test(c)) return 'devanagari';
    if (/[\u0e00-\u0e7f]/.test(c)) return 'thai';
    if (/[\u0370-\u03ff]/.test(c)) return 'greek';
    if (/[\u0400-\u04ff]/.test(c)) return 'cyrillic';
    if (/\p{Extended_Pictographic}/u.test(c)) return 'emoji';
    return 'other';
}

function diag(p) { try { if (window.__aeDiagnostic) window.__aeDiagnostic(p); } catch (e) {} }

function noteBuild(res, ms) {
    TEL.builds++;
    const base = { kind: state.kind, w: Math.round(res.widthMm), h: Math.round(res.heightMm) };
    if (TEL.builds === 1) {
        track('first_build', Object.assign({ ms }, base));
        try { if (window.__stage) window.__stage('built'); } catch (e) {}
    }
    if (ms > 1500 && TEL.slow++ < 3) diag(Object.assign({ issue: 'slow_build', ms, px: res.w * res.h }, base));
    const sig = issueSig(res);
    if (sig !== TEL.lastSig && TEL.verdicts++ < 20) {
        TEL.lastSig = sig;
        track('verdict', Object.assign({
            v: sig || 'ok', mat: state.material,
            islands: res.islandsFound, bridges: res.bridges.length,
        }, base));
    }
    for (const ch of (res.missing || [])) {
        const sc = scriptOf(ch);
        if (TEL.missing.has(sc)) continue;
        TEL.missing.add(sc);
        diag({ issue: 'glyph_missing', script: sc, face: state.face });
    }
}
let building = false;
let queued = false;
let seq = 0;

/* ── утилиты ──────────────────────────────────────────────────────────── */

function mmFromUnit(v, unit) {
    if (unit === 'cm') return v * 10;
    if (unit === 'in') return v * 25.4;
    return v;
}
function unitFromMm(mm, unit) {
    if (unit === 'cm') return mm / 10;
    if (unit === 'in') return mm / 25.4;
    return mm;
}
function fmt(v) { return Math.round(v * 10) / 10; }

function t(key, fallback) { return T[key] || fallback || key; }

/* ── пересборка ───────────────────────────────────────────────────────── */

function scheduleBuild() {
    if (building) { queued = true; return; }
    building = true;
    // готовый набор собран по ПРОШЛЫМ настройкам — после любой правки он чужой
    resetSetReady();
    const my = ++seq;
    const t0 = performance.now();
    setBusy(true);
    Promise.resolve()
        .then(() => buildStencil(sourceSpec(), settingsSpec()))
        .then((res) => {
            if (my !== seq) return;
            last = res;
            /* Испытательный шов. Сторож меряет ТОПОЛОГИЮ ИТОГОВОЙ МАСКИ —
               остались ли острова, какой ширины вышла перемычка, — и сделать
               это по картинке нельзя: на превью в 400 px перемычка в 1 мм и в
               4 мм неразличимы. Отдавать маску наружу дешевле, чем городить
               второй разбор пикселей в тесте, который будет ошибаться иначе,
               чем код. */
            window.__stencilLast = res;
            window.__stencilBuilds = (window.__stencilBuilds || 0) + 1;
            syncSizeFromResult();
            noteBuild(res, Math.round(performance.now() - t0));
            paint();
            paintReport();
            paintSheets();
            checkFaceCoverage();
        })
        .catch((err) => {
            if (my !== seq) return;
            if (window.__handleError) window.__handleError(err, 'stencil build');
            const box = $('stReport');
            /* `.st-bad` — метка ИСХОДА для сторожей: при исключении
               __stencilLast не появится никогда, и ждать надо её. */
            if (box) box.innerHTML = '<div class="st-vhead st-bad"><span class="st-badge bad">' + esc(t('err_build', 'Could not build the stencil')) + '</span></div>';
        })
        .finally(() => {
            building = false;
            setBusy(false);
            if (queued) { queued = false; scheduleBuild(); }
        });
}

let debTimer = null;
function rebuild(delay) {
    clearTimeout(debTimer);
    debTimer = setTimeout(scheduleBuild, delay == null ? 160 : delay);
}

function setBusy(on) {
    const el = $('stStage');
    if (el) el.classList.toggle('st-busy', !!on);
}

function sourceSpec() {
    if (state.kind === 'image') return { kind: 'image', bitmap: state.bitmap };
    if (state.kind === 'shape') return { kind: 'shape', shape: state.shape };
    return {
        kind: 'text', text: state.text || ' ', face: state.face,
        align: state.align, track: state.track, lineHeight: state.lineHeight, bold: true,
    };
}

function settingsSpec() {
    return {
        widthMm: state.widthMm, heightMm: state.heightMm, marginMm: state.marginMm,
        fit: state.lockRatio ? state.fitBy : false,
        material: state.material,
        bridges: state.bridges, bridgeMm: state.bridgeMm,
        bridgeDensityMm: state.bridgeDensityMm, maxPerIsland: state.maxPerIsland,
        removed: state.removed, manual: state.manual,
        mode: state.mode, threshold: state.threshold,
        edgeMm: state.edgeMm, growMm: state.growMm,
        invert: state.invert, mirror: state.mirror, smooth: state.smooth,
        cornerMm: state.cornerMm,
    };
}

/* ── превью ───────────────────────────────────────────────────────────── */

function paint() {
    const cv = $('stCanvas');
    if (!cv || !last) return;
    const wrap = $('stCanvasWrap') || cv.parentElement;
    const lifeWrap = $('stLifeWrap');
    const life = state.view === 'life';
    wrap.hidden = life;
    if (lifeWrap) lifeWrap.hidden = !life;
    paintSummaries();
    if (life) { paintLife(); return; }
    /* Рамка канвы получает высоту от раскладки (по экрану), канва вписывается
       в неё целиком. Раньше высота бралась как 52% окна независимо от места,
       и на широком экране канва 606×278 стояла посреди пустоты. */
    const cs = getComputedStyle(wrap);
    const padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
    const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    const availW = Math.max(120, wrap.clientWidth - padX);
    /* На широком рамка — остаток сцены после тулбара и дока (--st-fill:1 в
       CSS): её высота от канвы не зависит, и канва вписывается в clientHeight.
       На узком рамка растёт по канве, и доступное место — её потолок. */
    const fills = cs.getPropertyValue('--st-fill').trim() === '1';
    const maxH = parseFloat(cs.maxHeight);
    const availH = Math.max(90, (fills ? wrap.clientHeight : (isFinite(maxH) ? maxH : wrap.clientHeight)) - padY);
    const k = Math.min(availW / last.widthMm, availH / last.heightMm);
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const cw = Math.max(1, Math.floor(last.widthMm * k));
    const ch = Math.max(1, Math.floor(last.heightMm * k));
    cv.width = Math.round(cw * dpr);
    cv.height = Math.round(ch * dpr);
    cv.style.width = cw + 'px';
    cv.style.height = ch + 'px';

    const g = cv.getContext('2d');
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawInto(g, cw, ch, last, state.view, state.showRisk);
    cv.dataset.mmPerCssPx = String(last.widthMm / cw);
}

/** Одна отрисовка на все поверхности: превью, «как в жизни», PNG. */
/* Цвет листа по материалу. Вид «Трафарет» рисует ЛИСТ С ПРОРЕЗЯМИ, а не
   чёрные буквы на белом: так он выглядел ровно как напечатанная надпись, и
   не было видно, где материал, а где дыра (13.09.2026, «на настоящий
   трафарет не похоже»). В прорезях — стена, на которую ляжет краска;
   перемычки видны сами, как полоски листа поперёк буквы. */
const SHEET = {
    mylar:   { fill: '#bcd9e4', edge: 'rgba(40,90,110,.55)' },
    paper:   { fill: '#fbfaf5', edge: 'rgba(0,0,0,.35)' },
    card:    { fill: '#d6b98c', edge: 'rgba(90,60,20,.55)' },
    vinyl:   { fill: '#e8d44d', edge: 'rgba(110,90,0,.5)' },
    plastic: { fill: '#dcd6cc', edge: 'rgba(60,50,40,.5)' },
    fabric:  { fill: '#b9c4d4', edge: 'rgba(40,50,70,.5)' },
};

function drawInto(g, cw, ch, res, view, risk) {
    const sx = cw / res.widthMm, sy = ch / res.heightMm;
    const paintView = view === 'paint';
    g.save();

    const path = new Path2D();
    for (const r of res.rings) {
        path.moveTo(r[0][0] * sx, r[0][1] * sy);
        for (let i = 1; i < r.length; i++) path.lineTo(r[i][0] * sx, r[i][1] * sy);
        path.closePath();
    }
    if (view === 'cut') {
        g.fillStyle = '#ffffff';
        g.fillRect(0, 0, cw, ch);
        g.lineWidth = 1;
        g.strokeStyle = '#c0392b';
        g.stroke(path);
    } else if (paintView || view === 'print') {
        g.fillStyle = paintView ? '#f4f1ea' : '#ffffff';
        g.fillRect(0, 0, cw, ch);
        g.fillStyle = paintView ? '#20242c' : '#11151c';
        g.fill(path, 'evenodd');
    } else {
        g.fillStyle = '#6f747c';                       // стена в прорезях
        g.fillRect(0, 0, cw, ch);
        const sheet = SHEET[state.material] || SHEET.mylar;
        const cutout = new Path2D();
        cutout.rect(0, 0, cw, ch);
        cutout.addPath(path);
        g.save();
        g.shadowColor = 'rgba(0,0,0,.45)';
        g.shadowBlur = Math.max(2, Math.min(8, cw / 160));
        g.shadowOffsetY = Math.max(1, Math.min(4, cw / 300));
        g.fillStyle = sheet.fill;
        g.fill(cutout, 'evenodd');
        g.restore();
        g.lineWidth = 1;
        g.strokeStyle = sheet.edge;
        g.stroke(path);
    }
    if (risk && res.report && res.report.overlays && view !== 'cut') {
        drawOverlay(g, res, res.report.overlays.web, 'rgba(226,60,45,0.85)', sx, sy);
        drawOverlay(g, res, res.report.overlays.cut, 'rgba(240,160,20,0.9)', sx, sy);
    }
    g.restore();
}

function drawOverlay(g, res, mask, color, sx, sy) {
    if (!mask) return;
    const vd = res.report.verifyDims;
    if (!vd) return;
    const mmpp = res.report.verifyMmPerPx;
    let any = false;
    for (let i = 0; i < mask.length; i++) if (mask[i]) { any = true; break; }
    if (!any) return;
    g.save();
    g.fillStyle = color;
    const cell = Math.max(1, mmpp * sx);
    for (let y = 0; y < vd.h; y++) {
        for (let x = 0; x < vd.w; x++) {
            if (!mask[y * vd.w + x]) continue;
            g.fillRect(x * mmpp * sx, y * mmpp * sy, cell, cell);
        }
    }
    g.restore();
}

/**
 * Полоса «как в жизни»: кусок трафарета в НАСТОЯЩЕМ размере.
 * Без неё превью на 400 px для листа в 60 см — это 17 dpi, и толщина
 * перемычки в 2.5 мм на нём физически неотличима от 1 мм.
 */
/* Вид «1:1»: трафарет в настоящем размере в той же рамке, что канва. Центр
   окна — в миллиметрах рисунка (state.lifeX/Y), двигается перетаскиванием. */
function paintLife() {
    const cv = $('stLife');
    if (!cv || !last) return;
    const dpi = Number(($('stDpi') && $('stDpi').value) || 96);
    const pxPerMm = dpi / 25.4;
    const wrap = cv.parentElement;
    const cw = Math.max(120, wrap.clientWidth);
    const ch = Math.max(100, wrap.clientHeight);
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    cv.width = Math.round(cw * dpr); cv.height = Math.round(ch * dpr);
    const g = cv.getContext('2d');
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, cw, ch);

    const viewWmm = cw / pxPerMm, viewHmm = ch / pxPerMm;
    if (state.lifeX == null) { state.lifeX = last.widthMm / 2; state.lifeY = last.heightMm / 2; }
    const clampC = (c, total, view) => view >= total ? total / 2 : Math.max(view / 2, Math.min(total - view / 2, c));
    state.lifeX = clampC(state.lifeX, last.widthMm, viewWmm);
    state.lifeY = clampC(state.lifeY, last.heightMm, viewHmm);
    g.save();
    g.translate(cw / 2 - state.lifeX * pxPerMm, ch / 2 - state.lifeY * pxPerMm);
    g.fillStyle = '#fff';
    g.fillRect(0, 0, last.widthMm * pxPerMm, last.heightMm * pxPerMm);
    const p = new Path2D();
    for (const r of last.rings) {
        p.moveTo(r[0][0] * pxPerMm, r[0][1] * pxPerMm);
        for (let i = 1; i < r.length; i++) p.lineTo(r[i][0] * pxPerMm, r[i][1] * pxPerMm);
        p.closePath();
    }
    g.fillStyle = '#11151c';
    g.fill(p, 'evenodd');
    g.restore();

    // линейка 10 мм: доказательство, что вид действительно в масштабе
    g.strokeStyle = 'rgba(120,130,150,.9)';
    g.fillStyle = 'rgba(120,130,150,.95)';
    g.lineWidth = 1.5;
    g.beginPath();
    g.moveTo(10, ch - 12); g.lineTo(10 + 10 * pxPerMm, ch - 12);
    g.moveTo(10, ch - 17); g.lineTo(10, ch - 7);
    g.moveTo(10 + 10 * pxPerMm, ch - 17); g.lineTo(10 + 10 * pxPerMm, ch - 7);
    g.stroke();
    g.font = '12px system-ui, sans-serif';
    g.fillText('10 ' + t('u_mm', 'mm'), 14 + 10 * pxPerMm, ch - 8);
}

function wireLife() {
    const cv = $('stLife');
    if (!cv) return;
    let from = null;
    cv.addEventListener('pointerdown', (e) => {
        from = { x: e.clientX, y: e.clientY, lx: state.lifeX, ly: state.lifeY };
        cv.setPointerCapture(e.pointerId);
        cv.style.cursor = 'grabbing';
    });
    cv.addEventListener('pointermove', (e) => {
        if (!from || !last) return;
        const pxPerMm = Number(($('stDpi') && $('stDpi').value) || 96) / 25.4;
        state.lifeX = from.lx - (e.clientX - from.x) / pxPerMm;
        state.lifeY = from.ly - (e.clientY - from.y) / pxPerMm;
        paintLife();
    });
    const end = () => { from = null; cv.style.cursor = ''; };
    cv.addEventListener('pointerup', end);
    cv.addEventListener('pointercancel', end);
}

/* Краткое значение в заголовке каждой секции: закрытая секция не прячет
   состояние, она лишь не занимает место. */
function paintSummaries() {
    const put = (id, text) => { const el = $(id); if (el) el.textContent = text; };
    const u = unitLabel();
    if (state.kind === 'text') put('stSumSource', (state.text || '').replace(/\s+/g, ' ').trim().slice(0, 24));
    else if (state.kind === 'shape') put('stSumSource', t('sh_' + state.shape, state.shape));
    else put('stSumSource', state.fileName || t('src_image', 'Image'));
    if (last) {
        let sz = fmt(unitFromMm(last.widthMm, state.unit)) + ' × ' + fmt(unitFromMm(last.heightMm, state.unit)) + ' ' + u;
        if (state.kind === 'text' && last.letterMm) sz += ' · ↕' + fmt(unitFromMm(last.letterMm, state.unit)) + ' ' + u;
        put('stSumSize', sz);
    }
    const ms = $('stMaterial');
    const mat = ms && ms.selectedOptions[0] ? ms.selectedOptions[0].textContent.split(',')[0].trim() : state.material;
    put('stSumMaterial', mat + (state.bridges ? ' · ' + state.bridgeMm + ' ' + t('u_mm', 'mm') : ''));
    const pageLabel = ($('stPage') && $('stPage').selectedOptions[0]) ? $('stPage').selectedOptions[0].textContent : 'A4';
    put('stSumPrint', state.tile ? pageLabel + ' × ' + sheetCount() : pageLabel);
}

/* ── отчёт проверки ───────────────────────────────────────────────────── */

function paintReport() {
    const box = $('stReport');
    if (!box || !last) return;
    const r = last.report;
    const n = last.bridges.length;

    /* Док: одна строка вердикта со сводкой размеров, под ней — только
       замечания, каждое своим цветом (тем же, что пятно на канве). Раньше это
       была отдельная карточка из блоков по 50 px на замечание — и «Скачать»
       уезжала за экран ровно тогда, когда было что сказать. */
    const issues = [];
    for (const iss of r.issues) {
        if (iss.kind === 'island') {
            issues.push(['bad', t('ver_island', 'Loose pieces: {n}').replace('{n}', iss.count), t('ver_island_note', 'These parts will fall out. Turn bridges on or widen them.')]);
        } else if (iss.kind === 'thin_web') {
            issues.push(['bad', t('ver_thin_web', 'Material thinner than {mm} mm').replace('{mm}', iss.mm), t('ver_thin_web_note', 'It will tear here. Enlarge the stencil, widen bridges, or pick a stronger material.'), 'lw']);
        } else if (iss.kind === 'thin_cut') {
            issues.push(['warn', t('ver_thin_cut', 'Cuts narrower than {mm} mm').replace('{mm}', iss.mm), t('ver_thin_cut_note', 'Paint will not pass and the blade may not fit. Enlarge, or thicken the drawing.'), 'lc']);
        } else if (iss.kind === 'small_hole') {
            issues.push(['warn', t('ver_small_hole', 'Openings under {mm} mm: {n}').replace('{mm}', iss.mm).replace('{n}', iss.count), t('ver_small_hole_note', 'Very small openings are hard to weed out.')]);
        } else if (iss.kind === 'unmeasured') {
            issues.push(['info', t('ver_unmeasured', 'Not measured at this size'), t('ver_unmeasured_note', 'The stencil is too large for the check to resolve {mm} mm. The export is unaffected.').replace('{mm}', iss.mm)]);
        }
    }
    if (last.unresolved.length) {
        issues.push(['bad', t('ver_nobridge', 'No bridge found for {n} piece(s)').replace('{n}', last.unresolved.length), t('ver_nobridge_note', 'Increase the maximum bridge length or move the shape away from the edge.')]);
    }
    const info = [];
    info.push(t('st_size', 'Size') + ': ' + fmt(unitFromMm(last.widthMm, state.unit)) + ' × ' + fmt(unitFromMm(last.heightMm, state.unit)) + ' ' + unitLabel());
    if (last.letterMm) info.push(t('st_letter', 'Letter height') + ': ' + fmt(unitFromMm(last.letterMm, state.unit)) + ' ' + unitLabel());
    info.push(t('st_bridges', 'Bridges') + ': ' + n + (n ? ' (' + state.bridgeMm + ' ' + t('u_mm', 'mm') + ')' : ''));
    info.push(t('st_open', 'Open area') + ': ' + Math.round(r.stats.openArea * 100) + '%');
    const stats = '<span class="st-stats">' + info.map(s => '<span>' + esc(s) + '</span>').join('') + '</span>';

    const real = issues.filter(i => i[0] !== 'info');
    let head;
    if (!real.length) {
        head = '<span class="st-badge good">' + esc(t('ver_ok', 'Ready to cut')) + '</span>'
            + '<span class="st-vnote">' + esc(t('ver_ok_note', 'No islands, nothing below the material limits.')) + '</span>';
    } else {
        const worst = real.some(i => i[0] === 'bad') ? 'bad' : 'warn';
        head = '<span class="st-badge ' + worst + '">' + esc(t('ver_count', 'Check before cutting: {n}').replace('{n}', real.length)) + '</span>';
    }
    const list = issues.length
        ? '<ul class="st-issues">' + issues.map(i => '<li class="' + i[0] + '">'
            + (i[3] ? '<i class="st-sw ' + i[3] + '" aria-hidden="true"></i>' : '')
            + '<b>' + esc(i[1]) + '</b>' + esc(i[2]) + '</li>').join('') + '</ul>'
        : '';
    box.innerHTML = '<div class="st-vhead">' + head + stats + '</div>' + list;
    paintLegend();
}

/* Легенда пятен — на канве и только когда пятна есть: подпись к пустому
   холсту была шумом, а подпись под холстом читалась отдельно от цвета. */
function paintLegend() {
    const leg = $('stLegend');
    if (!leg) return;
    const ov = last && last.report && last.report.overlays;
    const has = (m) => { if (!m) return false; for (let i = 0; i < m.length; i++) if (m[i]) return true; return false; };
    const web = !!(ov && has(ov.web)), cut = !!(ov && has(ov.cut));
    const show = state.showRisk && state.view !== 'cut' && state.view !== 'life' && (web || cut);
    leg.hidden = !show;
    const w = leg.querySelector('[data-leg="web"]'), c = leg.querySelector('[data-leg="cut"]');
    if (w) w.hidden = !web;
    if (c) c.hidden = !cut;
    syncResetBtn();
}
function esc(s) { return String(s).replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c])); }
function unitLabel() { return state.unit === 'cm' ? t('u_cm', 'cm') : state.unit === 'in' ? t('u_in', 'in') : t('u_mm', 'mm'); }

/* ── печать: сколько листов ───────────────────────────────────────────── */

function pageSpec() {
    const p = PAGES[state.pageId] || PAGES.a4;
    const spec = (land) => ({ wMm: p.wMm, hMm: p.hMm, landscape: land, marginMm: 6, overlapMm: state.overlapMm });
    /* Ориентация листа решается сама, пока человек её не трогал: трафарет
       200×89 мм на книжном A4 раскладывался на ДВА листа, хотя альбомный
       вмещает его на одном (замер 13.09.2026, умолчание страницы). Выбранную
       руками ориентацию не переигрываем. */
    if (!state.landUser && last && state.tile) {
        const a = planTiles(last.widthMm, last.heightMm, spec(false));
        const b = planTiles(last.widthMm, last.heightMm, spec(true));
        const na = a ? a.tiles.length : Infinity, nb = b ? b.tiles.length : Infinity;
        state.landscape = nb < na;
        const cb = $('stLand'); if (cb) cb.checked = state.landscape;
    }
    return spec(state.landscape);
}

function paintSheets() {
    const el = $('stSheets');
    if (!el || !last) return;
    if (!state.tile) { el.textContent = t('sheet_single', 'One page, sized to the stencil'); return; }
    const plan = planTiles(last.widthMm, last.heightMm, pageSpec());
    if (!plan) { el.textContent = t('sheet_bad', 'Overlap is too large for this page'); return; }
    const n = plan.tiles.length;
    el.textContent = (n === 1
        ? t('sheet_one', 'Fits on one sheet')
        : t('sheet_n', '{n} sheets — {c} across x {r} down').replace('{n}', n).replace('{c}', plan.cols).replace('{r}', plan.rows))
        + (state.map && n > 1 ? ' + ' + t('sheet_map', 'assembly map') : '');
}

/* ── ручные перемычки ─────────────────────────────────────────────────── */

let drag = null;
function wireCanvas() {
    const cv = $('stCanvas');
    if (!cv) return;
    const pt = (ev) => {
        const r = cv.getBoundingClientRect();
        const cx = (ev.touches ? ev.touches[0].clientX : ev.clientX) - r.left;
        const cy = (ev.touches ? ev.touches[0].clientY : ev.clientY) - r.top;
        return { x: cx / r.width, y: cy / r.height };
    };
    const start = (ev) => {
        if (!last || $('stBridgeEdit') && !$('stBridgeEdit').checked) return;
        ev.preventDefault();
        const p = pt(ev);
        // клик по существующей перемычке — снять её
        const hit = findBridgeAt(p);
        if (hit >= 0) {
            const b = last.bridges[hit];
            if (b.auto) state.removed.push(b.key);
            else state.manual.splice(state.manual.indexOf(manualOf(b)), 1);
            rebuild(0);
            track('bridge_remove');
            return;
        }
        drag = { a: p, b: p };
    };
    const move = (ev) => {
        if (!drag) return;
        ev.preventDefault();
        drag.b = pt(ev);
        paint();
        const g = cv.getContext('2d');
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        g.setTransform(dpr, 0, 0, dpr, 0, 0);
        g.strokeStyle = '#2f9e44';
        g.lineWidth = Math.max(2, (state.bridgeMm / last.widthMm) * cv.clientWidth);
        g.lineCap = 'round';
        g.beginPath();
        g.moveTo(drag.a.x * cv.clientWidth, drag.a.y * cv.clientHeight);
        g.lineTo(drag.b.x * cv.clientWidth, drag.b.y * cv.clientHeight);
        g.stroke();
    };
    const end = () => {
        if (!drag) return;
        const d = drag; drag = null;
        const dist = Math.hypot((d.b.x - d.a.x) * last.widthMm, (d.b.y - d.a.y) * last.heightMm);
        if (dist < 1.5) { paint(); return; }
        state.manual.push({ ax: d.a.x, ay: d.a.y, bx: d.b.x, by: d.b.y });
        rebuild(0);
        track('bridge_add');
    };
    cv.addEventListener('pointerdown', start);
    cv.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
}

function manualOf(b) {
    return (state.manual || []).find(m =>
        Math.abs(m.ax * last.widthMm - b.ax) < 0.01 && Math.abs(m.ay * last.heightMm - b.ay) < 0.01);
}

function findBridgeAt(p) {
    if (!last) return -1;
    const x = p.x * last.widthMm, y = p.y * last.heightMm;
    let best = -1, bd = Math.max(4, state.bridgeMm * 1.8);
    last.bridges.forEach((b, i) => {
        const d = distToSeg(x, y, b.ax, b.ay, b.bx, b.by);
        if (d < bd) { bd = d; best = i; }
    });
    return best;
}

function distToSeg(px, py, ax, ay, bx, by) {
    const dx = bx - ax, dy = by - ay;
    const L = dx * dx + dy * dy;
    let t = L ? ((px - ax) * dx + (py - ay) * dy) / L : 0;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/* ── выдача ───────────────────────────────────────────────────────────── */

function outName(ext) {
    const base = state.kind === 'text'
        ? (state.text || 'stencil').split('\n')[0].slice(0, 28)
        : state.kind === 'shape' ? state.shape
            : (state.fileName || 'stencil').replace(/\.[^.]+$/, '');
    const size = Math.round(state.widthMm) + 'x' + Math.round(state.heightMm) + 'mm';
    const raw = 'stencil-' + base + '-' + size;
    if (window.__outputName) return window.__outputName.join([window.__outputName.sanitize(raw)], ext);
    return raw.replace(/[^\w\-]+/g, '-') + '.' + ext;
}

async function save(kind) {
    if (!last) return;
    const gate = window.__toolLimit && window.__toolLimit.begin(SLUG, limitKey());
    if (window.__toolLimit && !gate) return;
    try {
        let blob, name;
        if (kind === 'pdf') {
            const fontBytes = await pdfFont();
            blob = await buildPdf(last.rings, {
                widthMm: last.widthMm, heightMm: last.heightMm,
                page: pageSpec(), tile: state.tile, ruler: state.ruler, map: state.map,
                outline: false, title: outName('pdf'),
                fontBytes: fontBytes && fontBytes.bytes, fontkit: fontBytes && fontBytes.fontkit,
                strings: {
                    ruler: t('pdf_ruler', '50 mm — check the print scale'),
                    sheet: t('pdf_sheet', 'Sheet {n}/{total} — row {r}, column {c}'),
                    mapTitle: t('pdf_map', 'Assembly map'),
                    mapInfo: t('pdf_mapinfo', '{w} x {h} mm — {n} sheets, {c} across x {r} down'),
                    mapHint: t('pdf_maphint', 'Print at 100% (no "fit to page"). Trim each sheet along the dashed line and overlap it onto the next, matching the crosses.'),
                },
            });
            name = outName('pdf');
        } else if (kind === 'svg' || kind === 'svgcut') {
            const s = toSvg(last.rings, {
                widthMm: last.widthMm, heightMm: last.heightMm,
                variant: kind === 'svgcut' ? 'cut' : 'fill', title: outName('svg'),
            });
            blob = new Blob([s], { type: 'image/svg+xml' });
            name = outName(kind === 'svgcut' ? 'cut.svg' : 'svg');
        } else if (kind === 'dxf') {
            blob = new Blob([toDxf(last.rings, { widthMm: last.widthMm, heightMm: last.heightMm })],
                { type: 'application/dxf' });
            name = outName('dxf');
        } else {
            blob = await pngBlob();
            name = outName('png');
        }
        const okSave = await window.__downloadFile(blob, name);
        if (okSave !== false && gate) gate.commit();
        else if (gate) gate.abort();
        track('export', {
            f: kind, sheets: sheetCount(), kind: state.kind, v: issueSig(last) || 'ok',
            mat: state.material, mode: state.kind === 'image' ? state.mode : undefined,
            letter: last.letterMm ? Math.round(last.letterMm) : undefined,
            w: Math.round(last.widthMm), h: Math.round(last.heightMm), saved: okSave !== false ? 1 : 0,
        });
        if (window.__aeToolComplete) window.__aeToolComplete({ ok: true, f: kind, w: Math.round(last.widthMm), h: Math.round(last.heightMm), bridges: last.bridges.length });
    } catch (err) {
        if (gate) gate.abort();
        diag({ issue: 'export_failed', f: kind, err: String((err && err.message) || err).slice(0, 140), w: Math.round(last.widthMm), h: Math.round(last.heightMm) });
        if (window.__handleError) window.__handleError(err, 'stencil export ' + kind);
    }
}

function sheetCount() {
    if (!state.tile || !last) return 1;
    const p = planTiles(last.widthMm, last.heightMm, pageSpec());
    return p ? p.tiles.length : 1;
}

function limitKey() {
    /* Единица — рисунок, а не размер: тот же снимок, скачанный в другом
       размере или формате, — повтор, и повтор сегодня бесплатный (так
       написано на самой стене лимита). Раньше в ключе стояла ширина, и
       переход с A4 на A3 списывал второй запуск. */
    if (state.kind === 'image' && state.fileName) return 'image:' + state.fileName + '|' + (state.bitmap ? state.bitmap.width + 'x' + state.bitmap.height : '');
    return state.kind + ':' + (state.kind === 'text' ? state.text : state.shape);
}

async function pdfFont() {
    try {
        const PT = window.PdfTextFont;
        if (!PT || !PT.face) return null;
        const probe = t('pdf_sheet', 'Sheet');
        const id = PT.pick ? PT.pick(probe, 'noto') : 'noto';
        const f = await PT.face(id || 'noto');
        return f && f.bytes ? { bytes: f.bytes, fontkit: f.fontkit || window.fontkit } : null;
    } catch (e) { return null; }
}

async function pngBlob() {
    const ppm = Math.min(12, Math.sqrt(4000000 / (last.widthMm * last.heightMm)));
    const cw = Math.round(last.widthMm * ppm), ch = Math.round(last.heightMm * ppm);
    const c = document.createElement('canvas');
    c.width = cw; c.height = ch;
    // файл — чертёж для печати и резки: чёрный вырез на белом, без «листа» превью
    drawInto(c.getContext('2d'), cw, ch, last, state.view === 'paint' ? 'paint' : 'print', false);
    return new Promise(res => c.toBlob(res, 'image/png'));
}

function track(name, data) {
    try { if (window.__aeFunnel) window.__aeFunnel(name, data || {}); } catch (e) {}
}

/* ── разметка настроек ────────────────────────────────────────────────── */

function fillShapes() {
    const box = $('stShapes');
    if (!box) return;
    box.innerHTML = '';
    for (const grp of SHAPE_GROUPS) {
        const items = SHAPES.filter(s => s.group === grp);
        if (!items.length) continue;
        const head = document.createElement('div');
        head.className = 'st-shapegrp';
        head.textContent = t('shg_' + grp, grp);
        box.appendChild(head);
        const row = document.createElement('div');
        row.className = 'st-shaperow';
        for (const s of items) {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'st-shape' + (s.id === state.shape ? ' on' : '');
            b.dataset.shape = s.id;
            b.title = t('sh_' + s.id, s.id);
            b.setAttribute('aria-label', t('sh_' + s.id, s.id));
            /* То же правило, что у маски (pipeline.js): кнопка показывает ровно
               то, что вырежется. При evenodd «Кольцо» на кнопке было кольцом, а в
               файле — кругом (shapes.js, «правило заливки одно»). */
            b.innerHTML = '<svg viewBox="0 0 100 100" aria-hidden="true"><path d="' + s.d + '" fill="currentColor" fill-rule="nonzero"/></svg>';
            /* Подпись видна всегда: на касании всплывающей подсказки нет, и
               «Корону» с «Ёлкой» просили, глядя прямо на них (очередь 15.10). */
            const lbl = document.createElement('span');
            lbl.className = 'st-shape-l';
            lbl.setAttribute('aria-hidden', 'true');
            lbl.textContent = t('sh_' + s.id, s.id);
            b.appendChild(lbl);
            b.addEventListener('click', () => {
                state.shape = s.id;
                state.removed = []; state.manual = [];
                box.querySelectorAll('.st-shape').forEach(x => x.classList.toggle('on', x.dataset.shape === s.id));
                track('shape_pick', { s: s.id });
                syncShapeExtras();
                rebuild(0);
            });
            row.appendChild(b);
        }
        box.appendChild(row);
    }
}

/* Письменности, у которых гарнитура одна: они подставляются сами и в выборе
   не стоят. Раньше стояли — и «Noto Sans SC» на кириллице давал пустые рамки
   (скриншот 13.09.2026). Так же уже устроен выбор у /logo-maker. */
const SCRIPT_FACES = /^(cjk|korean|arabic|japanese)$/;
const FACE_ORDER = ['oswald', 'russoone', 'montserrat', 'yeseva', 'noto', 'playfair', 'comfortaa', 'inter',
    'lobster', 'marck', 'pacifico', 'caveat', 'anton', 'bebas', 'marker', 'roboto', 'poppins'];
let fontKey = null;

function pickableFaces() {
    const PT = window.PdfTextFont;
    return PT.faces().filter(f => !SCRIPT_FACES.test(f.id)).sort((a, b) => {
        const ia = FACE_ORDER.indexOf(a.id), ib = FACE_ORDER.indexOf(b.id);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
}

/**
 * Какие из выбираемых гарнитур набирают ТЕКУЩИЙ текст. Знаки, которых нет ни
 * в одной выбираемой (хангыль, арабский), в счёт не идут: их всё равно
 * наберёт своя гарнитура, и из-за них весь список не должен уезжать в «не
 * подходят».
 */
function facesThatFit(faces, text) {
    const PT = window.PdfTextFont;
    const chars = [...new Set(Array.from(String(text || '').replace(/\s/g, '')))];
    const style = chars.filter(ch => faces.some(f => PT.canRender(ch, f.id)));
    return faces.filter(f => style.every(ch => PT.canRender(ch, f.id)));
}

/**
 * Список шрифтов делится на «подходят» и «нет части букв». Пересобирается
 * только когда меняется САМ НАБОР подходящих, а не на каждую букву: открытый
 * список, который перестраивается под пальцем, закрывается на телефоне.
 */
function refreshFontList(force) {
    const sel = $('stFace');
    const PT = window.PdfTextFont;
    if (!sel || !PT || !PT.faces || !PT.faces().length) return;
    const faces = pickableFaces();
    const fits = facesThatFit(faces, state.text);
    const key = fits.map(f => f.id).join(',');
    if (!force && key === fontKey) return;
    fontKey = key;
    if (!faces.some(f => f.id === state.face)) state.face = (fits[0] || faces[0]).id;
    const opt = (f) => {
        const o = document.createElement('option');
        o.value = f.id;
        o.textContent = f.label;
        if (f.id === state.face) o.selected = true;
        return o;
    };
    sel.innerHTML = '';
    if (fits.length === faces.length || !fits.length) {
        faces.forEach(f => sel.appendChild(opt(f)));
    } else {
        const g1 = document.createElement('optgroup');
        g1.label = t('face_fits', 'Have every letter of your text');
        fits.forEach(f => g1.appendChild(opt(f)));
        const g2 = document.createElement('optgroup');
        g2.label = t('face_other', 'Missing some letters (they will be substituted)');
        faces.filter(f => !fits.includes(f)).forEach(f => g2.appendChild(opt(f)));
        sel.appendChild(g1);
        sel.appendChild(g2);
    }
    sel.value = state.face;
}

function fillFonts() {
    if (!$('stFace') || !window.PdfTextFont) return;
    window.PdfTextFont.load().then(() => { refreshFontList(true); checkFaceCoverage(); }).catch(() => {});
}

/**
 * Что произошло со шрифтом — сообщаем ПОСЛЕ сборки, по факту.
 *
 * Два случая. Знаки, которых нет НИ В ОДНОЙ гарнитуре, — их не нарисовать, и
 * молчать нельзя. Знаки, которых нет в ВЫБРАННОЙ, но есть в другой выбираемой
 * (кириллица в Anton), — подмена, и её надо назвать. Хангыль или арабский в
 * своей единственной гарнитуре — не подмена, а норма, об этом не пишем.
 *
 * ⚠️ Здесь жил переспрос таблицы CJK с флагом «один раз» — заслон от
 * бесконечной очереди микрозадач на корейской странице (12.09.2026). Он
 * больше не нужен: таблицу для отложенной гарнитуры теперь приносит сама
 * сборка (`PT.ready(text, face)`), а эта функция вызывается ПОСЛЕ сборки и
 * ничего не планирует.
 */
function checkFaceCoverage() {
    const warn = $('stFaceWarn');
    const PT = window.PdfTextFont;
    if (!warn || !PT || !PT.faces || !PT.faces().length) return;
    const txt = (state.text || '').replace(/\s/g, '');
    if (!txt || state.kind !== 'text') { warn.hidden = true; return; }

    const miss = (last && last.missing) || [];
    if (miss.length) {
        warn.hidden = false;
        warn.textContent = t('face_missing', 'These characters could not be set: {name}')
            .replace('{name}', miss.join('').slice(0, 24));
        return;
    }
    const faces = pickableFaces();
    const chosen = faces.find(f => f.id === state.face);
    if (!chosen || facesThatFit(faces, txt).includes(chosen)) { warn.hidden = true; return; }
    const need = Array.from(txt)
        .filter(ch => !PT.canRender(ch, chosen.id) && faces.some(f => PT.canRender(ch, f.id)))
        .join('');
    const altId = PT.pick(need, null);
    const alt = altId && PT.faceMeta ? PT.faceMeta(altId) : null;
    const swapKey = chosen.id + '>' + altId;
    if (!TEL.swaps.has(swapKey)) { TEL.swaps.add(swapKey); track('face_swapped', { from: chosen.id, to: altId || '', script: scriptOf(need) }); }
    warn.hidden = false;
    warn.textContent = t('face_try', 'Part of the text is set in {name}.')
        .replace('{name}', (alt && alt.label) || '');
}

/* ── привязки ─────────────────────────────────────────────────────────── */

function bindTabs() {
    document.querySelectorAll('[data-sttab]').forEach(btn => {
        btn.addEventListener('click', () => {
            const k = btn.dataset.sttab;
            state.kind = k;
            state.removed = []; state.manual = [];
            document.querySelectorAll('[data-sttab]').forEach(b => {
                const on = b.dataset.sttab === k;
                b.classList.toggle('on', on);
                b.setAttribute('aria-selected', on ? 'true' : 'false');
            });
            /* Смена вкладки — через общий примитив View Transitions: он сам
               проверяет поддержку, скрытую вкладку и reduced-motion и гасит
               отказы промисов; без него панели меняются мгновенно. */
            const swap = () => document.querySelectorAll('[data-stpane]').forEach(p => { p.hidden = p.dataset.stpane !== k; });
            if (window.__viewTransition) window.__viewTransition(swap); else swap();
            if (k === 'image' && !state.bitmap) { setBusy(false); return; }
            track('tab', { k });
            rebuild(0);
        });
    });
}

function num(el, fallback) {
    const v = parseFloat(String(el.value).replace(',', '.'));
    return isFinite(v) ? v : fallback;
}

/* После сборки поля размера показывают НАСТОЯЩИЙ лист: при сохранённых
   пропорциях второе измерение считает сборка. Поле, в котором человек сейчас
   печатает, не трогаем — иначе «15» превращалось бы в «150.3» на полуслове. */
function syncSizeFromResult() {
    if (!last) return;
    state.widthMm = last.widthMm;
    state.heightMm = last.heightMm;
    const act = document.activeElement;
    const put = (id, mm) => {
        const el = $(id);
        if (el && el !== act && mm != null) el.value = fmt(unitFromMm(mm, state.unit));
    };
    put('stW', last.widthMm);
    put('stH', last.heightMm);
    put('stLetter', last.letterMm);
    const row = $('stRowLetter');
    if (row) row.hidden = !(state.kind === 'text' && last.letterMm);
}

function bindSize() {
    const wIn = $('stW'), hIn = $('stH'), uSel = $('stUnit'), lIn = $('stLetter');
    const sync = () => {
        wIn.value = fmt(unitFromMm(state.widthMm, state.unit));
        hIn.value = fmt(unitFromMm(state.heightMm, state.unit));
        if (lIn && last && last.letterMm) lIn.value = fmt(unitFromMm(last.letterMm, state.unit));
    };
    sync();
    wIn.addEventListener('input', () => {
        const v = mmFromUnit(num(wIn, NaN), state.unit);
        if (!(v > 0)) return;
        state.widthMm = Math.min(3000, v);
        state.fitBy = 'w';
        rebuild();
    });
    hIn.addEventListener('input', () => {
        const v = mmFromUnit(num(hIn, NaN), state.unit);
        if (!(v > 0)) return;
        state.heightMm = Math.min(3000, v);
        state.fitBy = 'h';
        rebuild();
    });
    /* Высота букв — то, что человек знает про свой трафарет («номер дома
       10 см»); лист — производное. Буквы растут вместе с шириной рисунка,
       поэтому ширина считается из отношения, не зависящего от размера:
       высота букв / ширина рисунка у прошлой сборки. */
    if (lIn) lIn.addEventListener('input', () => {
        const v = mmFromUnit(num(lIn, NaN), state.unit);
        if (!(v > 0) || !last || !last.letterMm) return;
        const contentW = Math.max(1, last.widthMm - 2 * state.marginMm);
        const ratio = last.letterMm / contentW;
        state.lockRatio = true;
        const lk = $('stLock'); if (lk) lk.checked = true;
        state.widthMm = Math.min(3000, 2 * state.marginMm + v / ratio);
        state.fitBy = 'w';
        rebuild();
    });
    if (lIn) lIn.addEventListener('change', () => {
        if (last && last.letterMm) track('letter_height', { mm: Math.round(last.letterMm) });
    });
    uSel.addEventListener('change', () => { state.unit = uSel.value; sync(); paintReport(); });
    $('stLock').addEventListener('change', (e) => { state.lockRatio = e.target.checked; state.fitBy = 'w'; syncShapeExtras(); rebuild(0); });

    document.querySelectorAll('[data-stpreset]').forEach(b => {
        b.addEventListener('click', () => {
            const [w, h] = b.dataset.stpreset.split('x').map(Number);
            state.widthMm = w; state.heightMm = h;
            state.fitBy = 'box';
            sync();
            track('size_preset', { w, h });
            rebuild(0);
        });
    });
}

function bindControls() {
    const on = (id, ev, fn) => { const el = $(id); if (el) el.addEventListener(ev, fn); };

    on('stText', 'input', (e) => { state.text = e.target.value; state.removed = []; state.manual = []; refreshFontList(false); rebuild(); });
    on('stFace', 'change', (e) => { state.face = e.target.value; state.removed = []; state.manual = []; track('font', { f: state.face }); rebuild(0); });
    on('stAlign', 'change', (e) => { state.align = e.target.value; rebuild(0); });
    on('stTrack', 'input', (e) => { state.track = Number(e.target.value) / 100; label('stTrackV', e.target.value + '%'); rebuild(); });
    on('stLine', 'input', (e) => { state.lineHeight = Number(e.target.value) / 100; label('stLineV', e.target.value + '%'); rebuild(); });

    on('stMaterial', 'change', (e) => {
        state.material = e.target.value;
        const m = MATERIALS[state.material];
        if (m) { state.bridgeMm = m.bridge; const b = $('stBridgeW'); if (b) { b.value = String(m.bridge); label('stBridgeWV', m.bridge + ' ' + t('u_mm', 'mm')); } }
        track('material', { m: state.material });
        rebuild(0);
    });
    on('stMargin', 'input', (e) => { state.marginMm = Number(e.target.value); label('stMarginV', e.target.value + ' ' + t('u_mm', 'mm')); rebuild(); });

    on('stBridges', 'change', (e) => { state.bridges = e.target.checked; rebuild(0); });
    on('stBridgeW', 'input', (e) => { state.bridgeMm = Number(e.target.value); label('stBridgeWV', e.target.value + ' ' + t('u_mm', 'mm')); rebuild(); });
    on('stBridgeD', 'input', (e) => { state.bridgeDensityMm = Number(e.target.value); label('stBridgeDV', e.target.value + ' ' + t('u_mm', 'mm')); rebuild(); });
    on('stBridgeReset', 'click', () => { state.removed = []; state.manual = []; track('bridge_reset'); syncResetBtn(); rebuild(0); });
    // Подсказка появляется вместе с режимом — поверх самого холста, где рисуют.
    on('stBridgeEdit', 'change', (e) => {
        const h = $('stEditHint');
        if (h) h.hidden = !e.target.checked;
        const cv = $('stCanvas');
        if (cv) cv.style.cursor = e.target.checked ? 'crosshair' : '';
        if (e.target.checked) track('bridge_edit_on');
        syncResetBtn();
    });

    on('stMode', 'change', (e) => { state.mode = e.target.value; syncImageRows(); rebuild(0); });
    on('stThr', 'input', (e) => { state.threshold = Number(e.target.value); label('stThrV', e.target.value); rebuild(); });
    on('stThrAuto', 'click', () => { state.threshold = null; const s = $('stThr'); if (s) s.value = '128'; label('stThrV', t('auto', 'auto')); rebuild(0); });
    on('stEdge', 'input', (e) => { state.edgeMm = Number(e.target.value) / 10; label('stEdgeV', (Number(e.target.value) / 10) + ' ' + t('u_mm', 'mm')); rebuild(); });
    on('stGrow', 'input', (e) => { state.growMm = Number(e.target.value) / 10; label('stGrowV', (Number(e.target.value) / 10) + ' ' + t('u_mm', 'mm')); rebuild(); });
    on('stSmooth', 'input', (e) => { state.smooth = Number(e.target.value); rebuild(); });

    on('stInvert', 'change', (e) => { state.invert = e.target.checked; state.removed = []; state.manual = []; rebuild(0); });
    on('stMirror', 'change', (e) => { state.mirror = e.target.checked; state.removed = []; state.manual = []; track('mirror'); rebuild(0); });

    document.querySelectorAll('[data-stview]').forEach(b => b.addEventListener('click', () => {
        state.view = b.dataset.stview;
        document.querySelectorAll('[data-stview]').forEach(x => x.classList.toggle('on', x === b));
        track('view', { v: state.view });
        paint();
        paintLegend();
    }));
    on('stRisk', 'change', (e) => { state.showRisk = e.target.checked; paint(); paintLegend(); });
    on('stDpi', 'change', paintLife);
    ['stPage', 'stTile', 'stLand', 'stOverlap', 'stMaterial', 'stBridges'].forEach(id => on(id, 'change', paintSummaries));

    on('stTile', 'change', (e) => { state.tile = e.target.checked; paintSheets(); });
    on('stPage', 'change', (e) => { state.pageId = e.target.value; paintSheets(); });
    on('stLand', 'change', (e) => { state.landscape = e.target.checked; state.landUser = true; paintSheets(); });
    on('stOverlap', 'input', (e) => { state.overlapMm = Number(e.target.value); label('stOverlapV', e.target.value + ' ' + t('u_mm', 'mm')); paintSheets(); });
    on('stRuler', 'change', (e) => { state.ruler = e.target.checked; });
    on('stMap', 'change', (e) => { state.map = e.target.checked; paintSheets(); });

    ['pdf', 'svg', 'svgcut', 'dxf', 'png'].forEach(k => on('stDl-' + k, 'click', () => save(k)));

    window.addEventListener('resize', () => { if (last) { paint(); } });
    bindPopovers();
    observeStage();
    bindSet();
    bindPaste();
}

/* «Вернуть автоматические» видна, когда есть что возвращать или идёт правка:
   постоянная кнопка рядом с переключателями была шумом в каждом кадре. */
function syncResetBtn() {
    const b = $('stBridgeReset');
    if (!b) return;
    const editing = !!($('stBridgeEdit') && $('stBridgeEdit').checked);
    b.hidden = !(editing || (state.removed && state.removed.length) || (state.manual && state.manual.length));
}

/* Поповеры дока — <details>: открытие даёт браузер, закрытие по клику мимо и
   по Escape — здесь. Фокус возвращается на заголовок, иначе клавиатура теряет
   место. */
function bindPopovers() {
    const open = () => document.querySelectorAll('.st-pop[open]');
    document.addEventListener('click', (e) => {
        open().forEach(d => { if (!d.contains(e.target)) d.open = false; });
    });
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        open().forEach(d => { d.open = false; const s = d.querySelector('summary'); if (s) s.focus(); });
    });
    const pr = $('stSecPrint');
    if (pr) pr.addEventListener('toggle', () => { if (pr.open) track('print_open'); });
}

/* Канва следует за своей рамкой, а не только за окном: рамка меняет высоту,
   когда в доке появляется или уходит замечание, и без наблюдателя канва
   оставалась прежнего размера — вылезала за рамку или висела с полем. */
function observeStage() {
    if (!('ResizeObserver' in window)) return;
    let lw = 0, lh = 0, raf = 0;
    const ro = new ResizeObserver(() => {
        const el = state.view === 'life' ? $('stLifeWrap') : $('stCanvasWrap');
        if (!el || !last) return;
        const w = el.clientWidth, h = el.clientHeight;
        if (Math.abs(w - lw) < 2 && Math.abs(h - lh) < 2) return;
        lw = w; lh = h;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => paint());
    });
    ['stCanvasWrap', 'stLifeWrap'].forEach(id => { const el = $(id); if (el) ro.observe(el); });
}

/* Картинка из буфера обмена: скриншот или копия из браузера не требует
   сохранить файл на диск. Идёт через ИНПУТ общего загрузчика, а не мимо него:
   так строка «файл загружен» и чтение байтов остаются за примитивом. */
function bindPaste() {
    document.addEventListener('paste', (e) => {
        const tg = e.target;
        if (tg && (tg.tagName === 'TEXTAREA' || (tg.tagName === 'INPUT' && tg.type !== 'checkbox' && tg.type !== 'range') || tg.isContentEditable)) return;
        const files = e.clipboardData && e.clipboardData.files;
        const f = files && Array.from(files).find(x => /^image\//.test(x.type));
        if (!f) return;
        const input = document.querySelector('#stDrop input[type=file]');
        if (!input || typeof DataTransfer === 'undefined') return;
        e.preventDefault();
        try {
            const dt = new DataTransfer();
            dt.items.add(new File([f], f.name && f.name !== 'image.png' ? f.name : 'clipboard.png', { type: f.type }));
            input.files = dt.files;
            input.dispatchEvent(new Event('change', { bubbles: true }));
            track('image_paste');
        } catch (err) { if (window.__handleError) window.__handleError(err, 'stencil paste'); }
    });
}

/* ── набор «знак на лист» ─────────────────────────────────────────────────
   Пакет — перк плана: число знаков в наборе из config/tiers.php
   (stencil_set_caps), какое применяется — решает __tierLimits.is_pro. Там,
   где план не продаётся (__PRO_HERE === false), перк не запирается: нечего
   предлагать взамен. */
const SETCFG = (window.__STENCIL__ && window.__STENCIL__.set) || {};
const SET_CANCEL = { cancelled: true };
let setJob = null, setBlob = null, setName = '', setGate = null, setKey = '';

function setCap() {
    const here = window.__PRO_HERE !== false;
    const pro = !!(window.__tierLimits && window.__tierLimits.is_pro === true);
    return (!here || pro) ? (SETCFG.paid || 0) : (SETCFG.free || 0);
}
function syncSetPerk() {
    const perk = $('stSetPerk');
    if (perk) perk.hidden = setCap() > 0;
}
function setProg(text) {
    const p = $('stSetProg');
    if (!p) return;
    p.textContent = text || '';
    p.hidden = !text;
}
function resetSetReady() {
    if (setJob) return;
    if (setGate) { try { setGate.abort(); } catch (e) {} }
    setBlob = null; setGate = null; setKey = '';
    const go = $('stSetGo');
    if (go) go.textContent = t('set_go', 'Build PDF');
    setProg('');
}
const yieldToUI = () => (window.scheduler && typeof window.scheduler.yield === 'function')
    ? window.scheduler.yield() : new Promise(r => setTimeout(r, 0));

function setSource(what) {
    if (what === 'digits') return '0123456789';
    if (what === 'alpha') return t('set_alphabet', 'ABCDEFGHIJKLMNOPQRSTUVWXYZ');
    return state.text || '';
}
function setPage() {
    const p = PAGES[state.pageId] || PAGES.a4;
    return { wMm: p.wMm, hMm: p.hMm, landscape: !!(state.landUser && state.landscape) };
}

function bindSet() {
    const go = $('stSetGo'), what = $('stSetWhat');
    if (!go) return;
    syncSetPerk();
    document.addEventListener('tier-bootstrap:ready', syncSetPerk);
    if (what) what.addEventListener('change', resetSetReady);
    go.addEventListener('click', () => {
        if (setJob) { setJob.cancel = true; return; }
        if (setBlob) { saveSet(); return; }
        if (setCap() <= 0) {
            track('set_upsell', { what: what ? what.value : '' });
            setProg(t('set_locked', 'A set with one character per sheet is part of {plan}.').replace('{plan}', SETCFG.plan || ''));
            if (SETCFG.proUrl) window.location.href = SETCFG.proUrl;
            return;
        }
        runSet(what ? what.value : 'text');
    });
}

async function runSet(what) {
    const go = $('stSetGo');
    let chars = setChars(setSource(what));
    if (!chars.length) { setProg(t('set_empty', 'There are no characters to make a set from.')); return; }
    const cap = setCap();
    const trimmed = chars.length > cap;
    if (trimmed) chars = chars.slice(0, cap);
    const key = 'set:' + state.face + ':' + chars.join('');
    /* Набор списывает ОДИН запуск: единица — набор, а не лист. Повтор того же
       набора сегодня бесплатен, как повтор трафарета. */
    const gate = window.__toolLimit && window.__toolLimit.begin(SLUG, key);
    if (window.__toolLimit && !gate) return;
    const job = setJob = { cancel: false };
    const t0 = performance.now();
    go.textContent = t('set_stop', 'Stop');
    try {
        const spec = (ch) => ({ kind: 'text', text: ch, face: state.face, align: 'center', track: 0, lineHeight: state.lineHeight, bold: true });
        const boxes = [];
        for (const ch of chars) {
            if (job.cancel) throw SET_CANCEL;
            boxes.push(await measureSource(spec(ch)));
        }
        const page = setPage();
        const plan = planCharSet(boxes, page, { marginMm: state.marginMm, ruler: state.ruler });
        if (!plan || !plan.items.length) throw new Error('set: nothing fits the page');
        const items = [];
        for (const it of plan.items) {
            if (job.cancel) throw SET_CANCEL;
            setProg(t('set_building', 'Building character {n} of {total}…').replace('{n}', items.length + 1).replace('{total}', plan.items.length));
            await yieldToUI();
            const res = await buildStencil(spec(chars[it.index]), Object.assign(settingsSpec(), {
                widthMm: it.widthMm, heightMm: it.heightMm, fit: 'w', removed: [], manual: [],
            }));
            items.push({ rings: res.rings, widthMm: res.widthMm, heightMm: res.heightMm, ok: res.report.ok && !res.unresolved.length });
        }
        if (job.cancel) throw SET_CANCEL;
        const fontBytes = await pdfFont();
        const blob = await buildPdfSet(items, {
            page, ruler: state.ruler, title: 'stencil-set',
            fontBytes: fontBytes && fontBytes.bytes, fontkit: fontBytes && fontBytes.fontkit,
            strings: { ruler: t('pdf_ruler', '50 mm — check the print scale'), setLabel: t('pdf_set_label', 'Character {n} of {total}') },
        });
        setBlob = blob; setGate = gate; setKey = key;
        const raw = 'stencil-set-' + (what === 'text' ? chars.slice(0, 12).join('') : what);
        setName = window.__outputName ? window.__outputName.join([window.__outputName.sanitize(raw)], 'pdf') : raw.replace(/[^\w\-]+/g, '-') + '.pdf';
        /* Скачивание — отдельным нажатием: сборка набора длится секунды, и
           активация исходного клика к её концу уже истекла. */
        go.textContent = t('set_save', 'Download set');
        setProg(t('set_ready', 'Set ready: {n} sheets.').replace('{n}', items.length)
            + (trimmed ? ' ' + t('set_trimmed', 'The set holds the first {cap} characters.').replace('{cap}', cap) : ''));
        track('set_built', { what, n: items.length, bad: items.filter(x => !x.ok).length, ms: Math.round(performance.now() - t0), trimmed: trimmed ? 1 : 0 });
    } catch (err) {
        if (gate) { try { gate.abort(); } catch (e) {} }
        go.textContent = t('set_go', 'Build PDF');
        if (err === SET_CANCEL) {
            setProg('');
            track('set_cancel', { what });
        } else {
            setProg(t('set_failed', 'The set could not be built. Please try again.'));
            diag({ issue: 'set_failed', what, n: chars.length, err: String((err && err.message) || err).slice(0, 140) });
            if (window.__handleError) window.__handleError(err, 'stencil set');
        }
    } finally {
        setJob = null;
    }
}

async function saveSet() {
    if (!setBlob) return;
    let gate = setGate;
    if (!gate && window.__toolLimit) {
        gate = window.__toolLimit.begin(SLUG, setKey);
        if (!gate) return;
    }
    const ok = await window.__downloadFile(setBlob, setName);
    if (ok !== false) {
        if (gate) gate.commit();
        track('set_export', { saved: 1 });
        if (window.__aeToolComplete) window.__aeToolComplete({ ok: true, f: 'set_pdf' });
        setGate = null;
    } else {
        if (gate) gate.abort();
        setGate = null;
    }
}

function label(id, text) { const el = $(id); if (el) el.textContent = text; }

function syncImageRows() {
    const showThr = state.mode === 'threshold';
    const showEdge = state.mode === 'edge';
    const set = (id, on) => { const el = $(id); if (el) el.hidden = !on; };
    set('stRowThr', showThr);
    set('stRowEdge', showEdge);
}

function bindUploader() {
    const zone = $('stDrop');
    if (!zone) return;
    /* ⚠️ Через общий примитив, а не голым addEventListener: он ПРОИГРЫВАЕТ
       файл, выбранный до подписки (`el.__pendingFile`). Голый слушатель этой
       гонки не видит — человек выбрал файл, обработчика ещё нет, и выбор
       пропадает молча. Ловит UploaderListenerRaceTest. */
    const takeFile = async (file) => {
        if (!file) return;
        try {
            const bmp = await createImageBitmap(file);
            state.bitmap = bmp;
            state.fileName = file.name || '';
            state.kind = 'image';
            state.removed = []; state.manual = [];
            // пропорции листа под картинку подгонит сборка, если они сохраняются
            state.fitBy = 'w';
            document.querySelectorAll('[data-sttab]').forEach(b => {
                const on = b.dataset.sttab === 'image';
                b.classList.toggle('on', on); b.setAttribute('aria-selected', on ? 'true' : 'false');
            });
            document.querySelectorAll('[data-stpane]').forEach(p => { p.hidden = p.dataset.stpane !== 'image'; });
            track('image_loaded');
            rebuild(0);
        } catch (err) {
            try { if (window.__aeBlocked) window.__aeBlocked({ reason: 'image_unreadable', type: (file.type || '').slice(0, 32), size: file.size || 0 }); } catch (e) {}
            if (window.__toast) window.__toast(t('err_image', 'Could not read this image'), 'error');
        }
    };
    if (typeof window.__bindUploaderFile === 'function') {
        window.__bindUploaderFile(zone, (file) => takeFile(file));
    } else {
        zone.addEventListener('tool-uploader:file', (ev) => takeFile(ev.detail && ev.detail.file));
    }
    zone.addEventListener('tool-uploader:clear', () => {
        state.bitmap = null; state.fileName = '';
        if (state.kind === 'image') { state.kind = 'text'; document.querySelector('[data-sttab="text"]').click(); }
    });
}

/* ── простая фигура: скругление и растяжение ─────────────────────────── */

/* Поле скругления — только у фигур с вершинами; подсказка «снимите пропорции»
   — только у растяжимой фигуры, пока пропорции держатся. */
function syncShapeExtras() {
    const s = shapeById(state.shape);
    const row = $('stRowCorner');
    if (row) row.hidden = !(s && s.corners);
    const hint = $('stStretchHint');
    if (hint) hint.hidden = !(s && s.stretch && state.lockRatio);
}

function bindCorner() {
    const el = $('stCorner');
    if (!el) return;
    el.value = String(state.cornerMm);
    el.addEventListener('input', () => {
        const v = num(el, NaN);
        if (!(v >= 0)) return;
        state.cornerMm = Math.min(500, v);
        rebuild();
    });
}

/* ── форма пожеланий ──────────────────────────────────────────────────── */

/* Половина заявок на фигуры просила то, что в библиотеке УЖЕ было: «корона»,
   «ёлка» (очередь 15.10). Общая форма заявки зовёт этот крюк, пока человек
   печатает, и показывает совпавшие фигуры кнопками; отправка остаётся.
   Сопоставление — shapes.js::matchShapes (чистое, под тестом). */
const shapeMatches = (text) => matchShapes(text, (id) => (T['sh_' + id] || '') + ' ' + (T['sha_' + id] || ''));

function pickShape(id) {
    const b = document.querySelector('#stShapes .st-shape[data-shape="' + id + '"]');
    if (!b) return;
    b.click();
    b.scrollIntoView({ block: 'nearest' });
}

/* Цифры и буквы с завитками — это текст рукописной гарнитурой, а не фигура. */
function pickScriptFace() {
    const sel = $('stFace');
    if (!sel) return;
    let fits = [];
    try { fits = facesThatFit(pickableFaces(), state.text).map(f => f.id); } catch (e) {}
    const id = ['lobster', 'marck', 'pacifico', 'caveat'].find(f => fits.includes(f) && sel.querySelector('option[value="' + f + '"]'));
    if (!id || id === state.face) return;
    sel.value = id;
    sel.dispatchEvent(new Event('change'));
}

function bindSuggest() {
    window.__toolRequestDeflect = window.__toolRequestDeflect || {};
    window.__toolRequestDeflect[SLUG] = (text) => shapeMatches(text).map(id => ({
        label: t('sh_' + id, id),
        onPick: () => pickShape(id),
    }));
    document.querySelectorAll('[data-st-sugpath]').forEach(btn => btn.addEventListener('click', () => {
        const to = btn.dataset.stSugpath;
        const tab = document.querySelector('[data-sttab="' + to + '"]');
        if (tab) tab.click();
        if (to === 'text') pickScriptFace();
        track('sug_path', { to });
        const target = to === 'text' ? $('stText') : document.querySelector('[data-stpane="image"]');
        if (target) {
            if (to === 'text') { try { target.focus({ preventScroll: true }); } catch (e) {} }
            target.scrollIntoView({ block: 'nearest' });
        }
    }));
}

/* ── старт ────────────────────────────────────────────────────────────── */

function boot() {
    /* Гарнитура, не успевшая к сборке, догоняет сама: текст дособерётся без
       участия человека. Иначе он остался бы с сообщением «эти знаки набрать
       не удалось» и без единой подсказки, что делать дальше. */
    setFaceReadyHook(() => { if (state.kind === 'text') rebuild(0); });
    bindTabs();
    bindSize();
    bindControls();
    bindUploader();
    bindSuggest();
    fillShapes();
    bindCorner();
    syncShapeExtras();
    fillFonts();
    wireCanvas();
    wireLife();
    syncImageRows();
    const ta = $('stText');
    if (ta) { ta.value = state.text; }
    if (window.__toolSettings) {
        try {
            const saved = window.__toolSettings.load(SLUG, 'material', null);
            if (saved && MATERIALS[saved]) { state.material = saved; const s = $('stMaterial'); if (s) s.value = saved; state.bridgeMm = MATERIALS[saved].bridge; }
        } catch (e) {}
        const ms = $('stMaterial');
        if (ms) ms.addEventListener('change', () => window.__toolSettings.save(SLUG, 'material', ms.value));
    }
    rebuild(0);
    try { if (window.__stage) window.__stage('ready'); } catch (e) {}
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
